import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useTema, type Tema } from "../lib/tema";
import {
  carregarMeuPerfil,
  cpfParcial,
  enviarMinhaFoto,
  formatarTelefone,
  removerMinhaFoto,
  REDES,
  salvarMeuPerfil,
  type MeuPerfil,
  type Redes,
} from "../lib/perfil";
import {
  carregarDadosProfissionais,
  salvarDadosProfissionais,
  type DadosProfissionais,
} from "../lib/irproposta";
import { buscarCep } from "../lib/cep";
import {
  cepCompleto,
  cpfValido,
  mascararCep,
  mascararCnpj,
  mascararCpf,
  mascararTelefone as mascararTel,
  mascararUf,
  soDigitos,
} from "../lib/mascaras";
import Carregando from "../componentes/Carregando";
import Rodape from "../componentes/Rodape";
import { Aviso, CAMPO, iniciaisDe, Recado, Secao, SetaDireita, Topo } from "../componentes/ui";

// Meu Perfil — a primeira tela do hub em que o aluno ESCREVE.
//
// O e-mail aparece e não se edita, e isso é decisão gravada na 000042: ele é a
// chave que liga o aluno às compras dele, e mexer ali o desligaria dos próprios
// cursos. A tela DIZ por que, em vez de mostrar um campo cinza e deixar o aluno
// adivinhar — vale para tudo que esta página bloqueia.
//
// ---------------------------------------------------------------------------
// CPF e RG: PREENCHIMENTO ÚNICO, e a regra mudou em 01/09
// ---------------------------------------------------------------------------
// Até 31/08 o CPF era só leitura para todo mundo, com o motivo ao lado ("vai em
// contrato e certificado, o suporte corrige"). Na reunião com o time da
// secretaria, Victor decidiu: **o aluno preenche uma vez o que está vazio, e
// depois não edita mais** (000137).
//
// O que forçou a mudança foi a trava 1 do certificado: ela exige nome, CPF,
// telefone, RG e nascimento — e o hub tinha CPF em **204 de 39.252** matriculados
// e nenhuma coluna de RG ou nascimento. Com o campo travado para todos, ninguém
// pediria documento nenhum.
//
// Então há DOIS estados por campo, e nunca um campo cinza: vazio é campo de
// digitar, com o aviso da trava dito ANTES de gravar; preenchido é FATO com o
// motivo ao lado. Nascimento não trava — ele não identifica ninguém sozinho, e
// errar a data é o tipo de coisa que a pessoa nota depois.
//
// O que salva salva na hora, por seção: um botão "Salvar tudo" no fim obrigaria a
// pessoa a rolar de volta e deixaria dúvida sobre o que foi gravado.
export default function Perfil() {
  const [perfil, setPerfil] = useState<MeuPerfil | null>(null);
  const [estado, setEstado] = useState<"carregando" | "pronto" | "sem-perfil" | "erro">("carregando");

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const p = await carregarMeuPerfil();
        if (!ativo) return;
        if (!p) {
          setEstado("sem-perfil");
          return;
        }
        setPerfil(p);
        setEstado("pronto");
      } catch {
        if (ativo) setEstado("erro");
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  if (estado === "carregando") return <Carregando texto="Abrindo o seu perfil…" />;

  if (estado === "erro") {
    return (
      <Aviso titulo="Algo deu errado" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Não conseguimos abrir o seu perfil agora. Recarregue a página; se continuar, fale com o suporte.
      </Aviso>
    );
  }

  if (estado === "sem-perfil" || !perfil) {
    return (
      <Aviso titulo="Não encontramos o seu cadastro" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Nenhuma compra está ligada a este e-mail, então ainda não há perfil para editar.
      </Aviso>
    );
  }

  return (
    <div className="min-h-screen">
      <Topo atual="inicio" nome={perfil.nome} avatarUrl={perfil.avatar_url} />

      <div className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        <Link
          to="/"
          className="mt-6 mb-4 inline-flex items-center gap-2 text-sm font-semibold text-cinza transition hover:text-titulo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Voltar ao início
        </Link>

        <header className="rise">
          <h1 className="text-2xl font-bold text-titulo sm:text-3xl">Meu perfil</h1>
          <p className="mt-1 text-cinza">Seus dados, sua foto e como você aparece no Contabilidade Facilitada.</p>
        </header>

        <div className="mt-7 flex flex-col gap-4">
          <Foto perfil={perfil} aoTrocar={(url) => setPerfil({ ...perfil, avatar_url: url })} />
          <DadosPessoais perfil={perfil} aoSalvar={setPerfil} />
          <SobreVoce perfil={perfil} aoSalvar={setPerfil} />
          <DadosProfissionaisSecao />
          <Aparencia />
          <Conta perfil={perfil} />
        </div>

        <Rodape />
      </div>
    </div>
  );
}

function Foto({ perfil, aoTrocar }: { perfil: MeuPerfil; aoTrocar: (url: string | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  async function escolher(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    // O input é limpo aqui e não no fim: sem isso, escolher a MESMA foto duas
    // vezes seguidas não dispara `change` e a tela parece travada.
    e.target.value = "";
    if (!arquivo) return;

    setRecado(null);
    setOcupado(true);
    const r = await enviarMinhaFoto(arquivo);
    setOcupado(false);
    if (r.ok) {
      aoTrocar(r.url);
      setRecado({ tipo: "ok", texto: "Foto atualizada." });
    } else {
      setRecado({ tipo: "erro", texto: r.mensagem });
    }
  }

  async function remover() {
    setRecado(null);
    setOcupado(true);
    const r = await removerMinhaFoto();
    setOcupado(false);
    if (r.ok) {
      aoTrocar(null);
      setRecado({ tipo: "ok", texto: "Foto removida. Voltamos a usar as suas iniciais." });
    } else {
      setRecado({ tipo: "erro", texto: r.mensagem });
    }
  }

  return (
    <Secao
      i={1}
      titulo="Sua foto"
      descricao="Aparece no menu da sua conta. JPG, PNG ou WEBP — a gente ajusta o tamanho."
    >
      <div className="flex flex-wrap items-center gap-6">
        <span className="grid size-24 shrink-0 place-items-center overflow-hidden rounded-full bg-laranja font-display text-2xl font-bold text-azul">
          {perfil.avatar_url ? (
            <img src={perfil.avatar_url} alt="" className="size-full object-cover" />
          ) : (
            iniciaisDe(perfil.nome)
          )}
        </span>

        <div className="flex flex-wrap gap-3">
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={escolher}
            className="hidden"
          />
          <button
            type="button"
            disabled={ocupado}
            onClick={() => input.current?.click()}
            className="pill-cta"
          >
            {ocupado ? "Enviando…" : perfil.avatar_url ? "Trocar foto" : "Escolher foto"}
          </button>
          {perfil.avatar_url && (
            <button type="button" disabled={ocupado} onClick={remover} className="btn-contorno">
              Remover
            </button>
          )}
        </div>
      </div>
      {recado && <Recado {...recado} />}
    </Secao>
  );
}

function DadosPessoais({
  perfil,
  aoSalvar,
}: {
  perfil: MeuPerfil;
  aoSalvar: (p: MeuPerfil) => void;
}) {
  const [nome, setNome] = useState(perfil.nome ?? "");
  const [telefone, setTelefone] = useState(formatarTelefone(perfil.telefone ?? ""));
  const [cpf, setCpf] = useState("");
  const [rg, setRg] = useState("");
  const [nascimento, setNascimento] = useState(perfil.nascimento ?? "");
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  // CPF e RG travam DEPOIS de preenchidos — decisão de Victor em 31/08, com a
  // mesma razão que já bloqueava o CPF: um dígito errado aparece meses depois num
  // documento assinado, e o aluno não descobre no dia.
  //
  // O campo de digitar só existe quando está vazio. Isso é o inverso de mostrar
  // um campo cinza: campo desabilitado faz o aluno tentar, desistir e abrir
  // chamado, e é a doutrina desta tela desde a 000042 — "a tela DIZ por que, em
  // vez de mostrar um campo cinza e deixar o aluno adivinhar".
  const cpfTravado = !!perfil.cpf;
  const rgTravado = !!perfil.rg;

  const mudou =
    nome !== (perfil.nome ?? "") ||
    telefone !== formatarTelefone(perfil.telefone ?? "") ||
    (!cpfTravado && cpf.trim() !== "") ||
    (!rgTravado && rg.trim() !== "") ||
    nascimento !== (perfil.nascimento ?? "");

  // Só recusa aqui o que é ERRO DE DIGITAÇÃO evidente, e a razão é a trava: com
  // preenchimento único, um CPF errado não tem volta pelo hub. A validação de
  // verdade é do banco (`cpf_valido`, desde a 000058) — esta é a segunda guarda,
  // do lado de cá, para o aluno corrigir antes de gravar. Não é a regra
  // duplicada: é conferência, e conferência não é segunda regra (000071).
  const cpfIncompleto = !cpfTravado && cpf.trim() !== "" && soDigitos(cpf).length !== 11;
  const cpfInvalido = !cpfTravado && soDigitos(cpf).length === 11 && !cpfValido(cpf);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setRecado(null);
    setOcupado(true);
    const r = await salvarMeuPerfil({
      nome,
      telefone,
      // Campo travado não é reenviado: reenviar o mesmo valor É aceito pelo banco
      // (000139), mas mandar o que não se pode mudar é pedir para o servidor
      // decidir algo que a tela já sabe.
      cpf: cpfTravado ? undefined : cpf,
      rg: rgTravado ? undefined : rg,
      nascimento,
    });
    setOcupado(false);
    if (r.ok) {
      aoSalvar(r.perfil);
      // Limpa os campos de digitação: eles saem da tela, porque o valor agora
      // está travado e passa a aparecer como fato. Sem isto o campo continuaria
      // ali com o texto digitado, dando a impressão de que ainda é editável.
      setCpf("");
      setRg("");
      setRecado({ tipo: "ok", texto: "Dados salvos." });
    } else {
      setRecado({ tipo: "erro", texto: r.mensagem });
    }
  }

  return (
    <Secao i={2} titulo="Dados pessoais" descricao="É assim que a gente te chama nas telas e nos e-mails.">
      <form onSubmit={enviar} className="flex flex-col gap-4">
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-tinta">Nome completo</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} className={CAMPO} required minLength={2} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-tinta">Telefone (WhatsApp)</span>
          <input
            value={telefone}
            onChange={(e) => setTelefone(formatarTelefone(e.target.value))}
            placeholder="(11) 99999-9999"
            inputMode="tel"
            className={CAMPO}
          />
          <span className="mt-1.5 block text-xs text-cinza">
            Usamos para falar com você sobre o seu curso. Deixe em branco se preferir só e-mail.
          </span>
        </label>

        {/* ── CPF ──────────────────────────────────────────────────────────
            Dois estados, e nunca um campo cinza: vazio é campo de digitar,
            preenchido é FATO com o motivo ao lado. */}
        {cpfTravado ? (
          <div data-cpf-travado className="rounded-lg border border-borda bg-chip/40 p-3">
            <span className="block text-sm font-semibold text-tinta">CPF</span>
            <span className="mt-0.5 block text-sm tabular-nums text-cinza">
              {cpfParcial(perfil.cpf)}
            </span>
            <span className="mt-1 block text-xs text-cinza">
              Já está cadastrado e vai no seu contrato e no certificado, por isso não muda aqui. Se
              estiver errado, fale com a secretaria.
            </span>
          </div>
        ) : (
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-tinta">CPF</span>
            <input
              data-cpf
              value={cpf}
              onChange={(e) => setCpf(mascararCpf(e.target.value))}
              placeholder="000.000.000-00"
              inputMode="numeric"
              autoComplete="off"
              className={CAMPO}
            />
            {/* O aviso da trava vem ANTES de gravar, não depois. Com
                preenchimento único, "você errou e não pode corrigir" é a pior
                mensagem possível — então ela é dita enquanto ainda dá tempo. */}
            <span className="mt-1.5 block text-xs text-cinza">
              Você preenche <strong className="font-semibold text-tinta">uma vez</strong>: depois de
              salvo, o CPF não muda por aqui, porque vai no seu contrato e no certificado. Confira
              antes de salvar.
            </span>
            {cpfInvalido && (
              <span className="mt-1.5 block text-xs font-semibold text-erro-tinta">
                Este CPF não é válido — confira os números.
              </span>
            )}
            {cpfIncompleto && (
              <span className="mt-1.5 block text-xs text-cinza">Faltam números.</span>
            )}
          </label>
        )}

        {/* ── RG ───────────────────────────────────────────────────────────
            Sem máscara e sem validação de formato, de propósito: cada estado
            emite num formato diferente (com letra, com ponto, com dígito X) e
            não existe aritmética de verificação como a do CPF. Inventar um
            formato aqui recusaria RG legítimo — é a cicatriz do handle em ASCII
            da 000101, que recusou 31 alunos chamados João. */}
        {rgTravado ? (
          <div data-rg-travado className="rounded-lg border border-borda bg-chip/40 p-3">
            <span className="block text-sm font-semibold text-tinta">RG</span>
            <span className="mt-0.5 block text-sm text-cinza">{perfil.rg}</span>
            <span className="mt-1 block text-xs text-cinza">
              Já está cadastrado e vai no seu certificado. Se estiver errado, fale com a secretaria.
            </span>
          </div>
        ) : (
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-tinta">
              RG <span className="font-normal text-cinza">(para o certificado da pós)</span>
            </span>
            <input
              data-rg
              value={rg}
              onChange={(e) => setRg(e.target.value)}
              placeholder="00.000.000-0"
              autoComplete="off"
              className={CAMPO}
            />
            <span className="mt-1.5 block text-xs text-cinza">
              Como está no documento — com pontos, letras ou o que houver. Você preenche{" "}
              <strong className="font-semibold text-tinta">uma vez</strong>.
            </span>
          </label>
        )}

        {/* ── Nascimento ───────────────────────────────────────────────────
            NÃO trava, e a diferença é deliberada: ele não identifica ninguém
            sozinho, e errar a data é o tipo de coisa que a pessoa nota depois. */}
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-tinta">
            Data de nascimento{" "}
            <span className="font-normal text-cinza">(para o certificado da pós)</span>
          </span>
          <input
            data-nascimento
            type="date"
            value={nascimento}
            onChange={(e) => setNascimento(e.target.value)}
            // O `max` corta a data no futuro e nos primeiros 14 anos, que é o
            // mesmo intervalo da constraint `nascimento_plausivel` (000137).
            // Cortar aqui evita a viagem até o servidor para receber um erro que
            // a tela já podia impedir.
            max={new Date(Date.now() - 14 * 365.25 * 864e5).toISOString().slice(0, 10)}
            min="1900-01-02"
            className={CAMPO}
          />
          <span className="mt-1.5 block text-xs text-cinza">
            Esta você pode corrigir depois.
          </span>
        </label>

        <button
          type="submit"
          disabled={ocupado || !mudou || cpfInvalido || cpfIncompleto}
          className="pill-cta self-start"
        >
          {ocupado ? "Salvando…" : "Salvar dados"}
        </button>
      </form>
      {recado && <Recado {...recado} />}
    </Secao>
  );
}

function SobreVoce({ perfil, aoSalvar }: { perfil: MeuPerfil; aoSalvar: (p: MeuPerfil) => void }) {
  const [bio, setBio] = useState(perfil.bio ?? "");
  const [redes, setRedes] = useState<Redes>(perfil.redes);
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  const LIMITE = 280;
  const restam = LIMITE - bio.length;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setRecado(null);
    setOcupado(true);
    // Envia `bio: ""` quando o aluno apagou tudo: na RPC, vazio significa
    // "apaga" e nulo significa "não mexe". A distinção é o que permite salvar
    // seção por seção sem uma seção sobrescrever a outra.
    const r = await salvarMeuPerfil({ bio, redes });
    setOcupado(false);
    if (r.ok) {
      aoSalvar(r.perfil);
      setRedes(r.perfil.redes);
      setRecado({ tipo: "ok", texto: "Tudo salvo." });
    } else {
      setRecado({ tipo: "erro", texto: r.mensagem });
    }
  }

  return (
    <Secao
      i={3}
      titulo="Sobre você"
      descricao="Uma apresentação curta e as suas redes. Por enquanto, só você vê — quando a Área do Candidato entrar no hub, é isto que vai formar o seu perfil profissional."
    >
      <form onSubmit={enviar} className="flex flex-col gap-5">
        <label className="block">
          <span className="mb-1.5 flex items-baseline justify-between gap-3 text-sm font-semibold text-tinta">
            Bio
            {/* "227 caracteres" lia como o que já foi escrito; é o que ainda
                cabe. A palavra "restam" resolve a ambiguidade. */}
            <span className={`text-xs font-medium tabular-nums ${restam < 20 ? "text-alerta-tinta" : "text-cinza"}`}>
              restam {restam}
            </span>
          </span>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
            maxLength={LIMITE}
            placeholder="Ex.: Contadora em Belo Horizonte, especialista em Departamento Pessoal."
            className={`${CAMPO} h-auto py-3 leading-relaxed`}
          />
        </label>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1.5 text-sm font-semibold text-tinta">Redes sociais</legend>
          {REDES.map((r) => (
            <label key={r.chave} className="block">
              <span className="mb-1 block text-xs font-semibold text-cinza">{r.rotulo}</span>
              {/* Prefixo fixo à esquerda, e o aluno digita só o usuário. Não é
                  enfeite: o banco guarda o handle justamente para que nenhuma URL
                  (nem um `javascript:`) entre por este campo. */}
              <span className="flex items-stretch overflow-hidden rounded-xl border-[1.5px] border-borda bg-superficie focus-within:border-laranja focus-within:ring-3 focus-within:ring-laranja/20">
                <span className="grid shrink-0 place-items-center border-r border-borda bg-chip/40 px-3 text-xs text-cinza">
                  {r.prefixo.replace("https://", "")}
                </span>
                <input
                  value={redes[r.chave] ?? ""}
                  onChange={(e) => setRedes({ ...redes, [r.chave]: e.target.value })}
                  placeholder={r.exemplo}
                  className="h-12 min-w-0 flex-1 bg-transparent px-3 text-tinta placeholder:text-cinza/60 focus:outline-none"
                />
              </span>
            </label>
          ))}

          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-cinza">Site ou portfólio</span>
            <input
              value={redes.site ?? ""}
              onChange={(e) => setRedes({ ...redes, site: e.target.value })}
              placeholder="https://seusite.com.br"
              inputMode="url"
              className={CAMPO}
            />
          </label>
        </fieldset>

        <button type="submit" disabled={ocupado} className="pill-cta self-start">
          {ocupado ? "Salvando…" : "Salvar"}
        </button>
      </form>
      {recado && <Recado {...recado} />}
    </Secao>
  );
}

function Aparencia() {
  const { tema, definir } = useTema();
  const opcoes: Array<{ id: Tema; rotulo: string; ajuda: string }> = [
    { id: "claro", rotulo: "Claro", ajuda: "Fundo claro, sempre" },
    { id: "escuro", rotulo: "Escuro", ajuda: "Fundo escuro, sempre" },
    { id: "sistema", rotulo: "Automático", ajuda: "Segue o seu aparelho" },
  ];

  return (
    <Secao
      i={5}
      titulo="Aparência"
      descricao="Vale neste aparelho. No celular, o automático acompanha o modo noturno."
    >
      <div role="radiogroup" aria-label="Aparência" className="grid gap-3 sm:grid-cols-3">
        {opcoes.map((o) => (
          <button
            key={o.id}
            role="radio"
            aria-checked={tema === o.id}
            onClick={() => definir(o.id)}
            className={`rounded-xl border-[1.5px] p-4 text-left transition ${
              tema === o.id
                ? "border-laranja bg-laranja/10"
                : "border-borda hover:border-laranja/50 hover:bg-chip/40"
            }`}
          >
            <span className="block font-display font-semibold text-titulo">{o.rotulo}</span>
            <span className="mt-0.5 block text-xs text-cinza">{o.ajuda}</span>
          </button>
        ))}
      </div>
    </Secao>
  );
}

/**
 * O que a tela mostra e não deixa mudar — com o motivo à vista.
 *
 * Campo desabilitado sem explicação faz o aluno tentar, desistir e abrir chamado
 * no suporte. Dizer "o e-mail é o que liga a sua conta às suas compras" resolve a
 * dúvida na hora e transforma o bloqueio em informação.
 */
function Conta({ perfil }: { perfil: MeuPerfil }) {
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function pedirTrocaDeSenha() {
    setRecado(null);
    setOcupado(true);
    const { error } = await supabase.auth.resetPasswordForEmail(perfil.email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });
    setOcupado(false);
    setRecado(
      error
        ? { tipo: "erro", texto: "Não foi possível enviar o link agora. Tente de novo em instantes." }
        : { tipo: "ok", texto: `Enviamos um link de troca de senha para ${perfil.email}.` },
    );
  }

  return (
    <Secao i={6} titulo="Conta e acesso">
      <dl className="flex flex-col divide-y divide-borda">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-3">
          <dt className="text-sm font-semibold text-tinta">E-mail</dt>
          <dd className="text-sm text-cinza">{perfil.email}</dd>
          <dd className="w-full text-xs text-cinza">
            É o endereço que liga a sua conta às suas compras, por isso não muda aqui. Precisa trocar? Fale
            com o suporte que a gente faz a troca dos dois lados.
          </dd>
        </div>

        {/* O CPF SAIU daqui em 01/09 e passou a morar em "Dados pessoais", onde
            agora ele é preenchível uma vez (000137). Mostrar nos dois lugares
            seria a mesma informação duas vezes, e o aluno procuraria a diferença
            entre elas — a cicatriz do índice em dois lugares na mesma tela do IR
            Pro. Aqui ficou só o que a conta tem e o aluno não decide: e-mail e
            senha. */}

        <div className="flex flex-wrap items-center justify-between gap-4 py-4">
          <div>
            <dt className="text-sm font-semibold text-tinta">Senha</dt>
            <dd className="mt-0.5 text-xs text-cinza">
              A mesma senha vale no portal e nas aulas — trocamos as duas juntas.
            </dd>
          </div>
          {/* Recado inline, não `alert()`: o alerta do navegador é modal, sai do
              desenho da tela e some sem deixar rastro do que aconteceu. */}
          <button onClick={pedirTrocaDeSenha} disabled={ocupado} className="btn-contorno">
            {ocupado ? "Enviando…" : "Trocar senha"} <SetaDireita />
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 py-4">
          <div>
            <dt className="text-sm font-semibold text-tinta">Sair</dt>
            <dd className="mt-0.5 text-xs text-cinza">Encerra a sessão neste navegador.</dd>
          </div>
          <button onClick={() => void supabase.auth.signOut()} className="btn-contorno">
            Sair da minha conta
          </button>
        </div>
      </dl>
      {recado && <Recado {...recado} />}
    </Secao>
  );
}

/**
 * Os dados do aluno como PRESTADOR de serviço (000065, `perfis_profissionais`).
 *
 * Existe porque o contrato de IRPF do IR Proposta tem dois blocos de identificação,
 * e o do CONTRATADO está VAZIO no modelo — rótulos "Nome:", "CNPJ:", "E-mail:" sem
 * campo nenhum, porque hoje o aluno preenche à mão. Sem estes dados, "o contrato já
 * vem preenchido" só valeria para metade do documento.
 *
 * Fica no Perfil, e não dentro do IR Proposta, por decisão de Victor em 19/08: é
 * dado do ALUNO, e qualquer outra ferramenta que emita documento em nome dele
 * (procuração, recibo, Legaliza Fácil) vai querer os mesmos campos. A segunda cópia
 * deles seria a hora de ter feito assim.
 *
 * Tudo opcional. Quem não tem escritório usa o hub igual, e o contrato cai no nome
 * da pessoa — o que é o certo para MEI e autônomo.
 */
function DadosProfissionaisSecao() {
  const [dados, setDados] = useState<DadosProfissionais | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  useEffect(() => {
    let ativo = true;
    void carregarDadosProfissionais().then((d) => ativo && setDados(d));
    return () => {
      ativo = false;
    };
  }, []);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!dados) return;
    setOcupado(true);
    setRecado(null);
    const r = await salvarDadosProfissionais(dados);
    setOcupado(false);
    if (!r.ok) return setRecado({ tipo: "erro", texto: r.mensagem });
    setDados(r.res);
    setRecado({ tipo: "ok", texto: "Dados profissionais salvos." });
  }

  if (!dados) return null;

  /**
   * As máscaras vêm de `lib/mascaras.ts`, as mesmas do IR Proposta e do formulário
   * público. Um CNPJ digitado aqui vai para o bloco CONTRATADO de um contrato
   * assinado; formato divergente entre as telas é o tipo de coisa que ninguém nota
   * até o documento sair.
   */
  const MASCARAS: Partial<Record<keyof DadosProfissionais, (v: string) => string>> = {
    cnpj: mascararCnpj,
    whatsapp: mascararTel,
    cep: mascararCep,
    estado: mascararUf,
  };

  async function completarPeloCep(cepDigitado: string) {
    if (!dados || !cepCompleto(cepDigitado)) return;
    const achado = await buscarCep(cepDigitado);
    if (!achado) return;
    // Nunca sobrescreve o que o aluno já escreveu — mesma regra do formulário
    // público: o ViaCEP não sabe onde é o escritório dele melhor que ele.
    setDados((atual) =>
      atual
        ? {
            ...atual,
            endereco: (atual.endereco ?? "").trim() || achado.endereco,
            bairro: (atual.bairro ?? "").trim() || achado.bairro,
            cidade: (atual.cidade ?? "").trim() || achado.cidade,
            estado: (atual.estado ?? "").trim() || achado.estado,
          }
        : atual,
    );
  }

  const campo = (
    chave: keyof DadosProfissionais,
    rotulo: string,
    extra?: { maxLength?: number; placeholder?: string; dica?: string },
  ) => (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-titulo">{rotulo}</span>
      <input
        value={dados[chave] ?? ""}
        onChange={(e) => {
          const mascara = MASCARAS[chave];
          const valor = mascara ? mascara(e.target.value) : e.target.value;
          setDados({ ...dados, [chave]: valor });
          if (chave === "cep" && cepCompleto(valor)) void completarPeloCep(valor);
        }}
        onBlur={() => {
          if (chave === "cep") void completarPeloCep(dados.cep ?? "");
        }}
        inputMode={chave === "cnpj" || chave === "cep" ? "numeric" : chave === "whatsapp" ? "tel" : undefined}
        maxLength={extra?.maxLength}
        placeholder={extra?.placeholder}
        className={CAMPO}
      />
      {extra?.dica && <span className="mt-1 block text-sm text-cinza">{extra.dica}</span>}
    </label>
  );

  return (
    <Secao
      titulo="Dados profissionais"
      descricao="Entram no bloco do contratado nos documentos que você gera aqui — como o contrato do IR Proposta. Todos são opcionais."
      i={4}
    >
      <form onSubmit={salvar}>
        <div className="grid gap-3 sm:grid-cols-2">
          {campo("escritorio", "Nome do escritório", {
            dica: "Em branco, o contrato sai no seu nome — o certo para MEI e autônomo.",
          })}
          {campo("cnpj", "CNPJ", { placeholder: "00.000.000/0000-00" })}
          {campo("crc", "CRC", { placeholder: "1SP123456/O-4" })}
          {campo("email", "E-mail comercial", {
            dica: "Em branco, usamos o e-mail da sua conta.",
          })}
          {campo("whatsapp", "WhatsApp comercial")}
          {campo("cep", "CEP")}
          {campo("endereco", "Endereço (rua e número)")}
          {campo("complemento", "Complemento")}
          {campo("bairro", "Bairro")}
          {campo("cidade", "Cidade", {
            dica: "É a cidade que assina o contrato, no fecho do documento.",
          })}
          {campo("estado", "Estado (UF)", { maxLength: 2, placeholder: "SP" })}
        </div>

        <button type="submit" disabled={ocupado} className="pill-cta mt-5 disabled:opacity-60">
          {ocupado ? "Salvando…" : "Salvar dados profissionais"}
        </button>
      </form>
      {recado && <Recado {...recado} />}
    </Secao>
  );
}
